import {Image} from '@/_application/core/components/ui/image';
import Link from '@/_application/core/components/ui/link';
import {DynamicPageServerSideProps} from '@/_application/core/types';
import {getBrandUrl, getImageAssetUrl} from '@/_application/core/utils/common';
import cn from 'classnames';
import {useState} from 'react';

const ViewMoreDisabledIcon = getImageAssetUrl('icons/icon-view-more-disabled.svg');
const ViewMoreIcon = getImageAssetUrl('icons/icon-view-more.svg');

interface IProps {
    variables: DynamicPageServerSideProps['variables'];
}

/**
 * CategoryBrandList - ISR Component
 *
 * Receives pre-fetched brands from server-side (ISR) via variables.initialData.categoryBrands
 * No client-side data fetching - pure SSR/ISR rendering
 * Uses CSS transitions instead of framer-motion to avoid hydration issues
 */
function CategoryBrandList({variables}: Readonly<IProps>) {
    // Use pre-fetched brands from ISR instead of client-side hook
    const categoryBrands = variables?.initialData?.categoryBrands ?? [];
    const [isViewMore, setIsViewMore] = useState(false);

    const validBrands = categoryBrands.filter(brand => brand?.url && brand.img);
    if (!validBrands.length) return null;

    return (
        <div className="relative mt-5 rounded-2xl bg-white px-[22px] py-5">
            <h3 className="text-[22px] font-extrabold leading-[25px] text-primary">Thương hiệu</h3>

            <div className="flex items-start">
                <div
                    className={cn(
                        'mt-5 flex flex-wrap items-start gap-x-[44px] gap-y-5 overflow-hidden transition-[max-height] duration-400 ease-out',
                        {
                            'w-[calc(100%-128px)]': validBrands.length > 6,
                            'w-full': validBrands.length <= 6,
                        },
                    )}
                    style={{
                        maxHeight: isViewMore ? '2000px' : '80px',
                    }}>
                    {validBrands.map(brand => {
                        if (!brand?.img || !brand.url) return null;
                        const brandUrl = getBrandUrl(brand.url);

                        return (
                            <Link
                                key={brand?.id}
                                href={brandUrl}
                                className="flex h-[80px] w-[128px] select-none items-center justify-center overflow-hidden rounded-[10px] border border-solid border-KPZ_turquoise py-2 transition-opacity hover:opacity-80">
                                <div className="flex h-20 w-20 items-center justify-center p-2">
                                    <div className="relative h-16 w-16">
                                        <Image src={brand.img} alt={brand?.label!} fill sizes="100%" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                {validBrands.length > 6 && (
                    <button
                        aria-label="Xem thêm / Thu gọn thương hiệu"
                        className={cn(
                            'mt-5 flex h-[80px] w-[128px] select-none flex-col items-center justify-center overflow-hidden rounded-[10px] border border-solid py-2',
                            {'border-KPZ_turquoise': isViewMore, 'border-KPZ_Border': !isViewMore},
                        )}
                        onClick={() => setIsViewMore(prev => !prev)}>
                        <div className="flex h-20 w-20 items-center justify-center p-2">
                            <Image
                                src={isViewMore ? ViewMoreIcon : ViewMoreDisabledIcon}
                                alt={'View more'}
                                width={36}
                                height={36}
                            />
                        </div>
                        <span className="text-base text-KPZ_text_1st">Xem tất cả</span>
                    </button>
                )}
            </div>
        </div>
    );
}

export default CategoryBrandList;
